import { ReactNode, useEffect, useMemo, useState } from "react";
import {
  Book,
  Calendar,
  DocumentText1,
  ExportSquare,
  ImportSquare,
  Notepad,
  Profile2User,
} from "iconsax-reactjs";
import StatGradientCard, {
  StatGradientTone,
} from "../../components/commons/stats/StatGradientCard";
import StatsChartPanel from "../../components/commons/stats/StatsChartPanel";
import { StatsBarChartDatum } from "../../components/commons/stats/StatsBarChart";
import { useAdminLoading } from "../../context/AdminLoadingContext";
import { useInitialPageLoad } from "../../components/layouts/admin-layout/useInitialPageLoad";
import { getHomeStats } from "../../services/webServices/stats/get";
import type { StatsObjectType } from "../../services/webServices/stats/types";
import { getDashboardData } from "../../services/webServices/dahsboard/get";
import type { GetDashboardDataResponse } from "../../services/webServices/dahsboard/types";

const OBJECT_META: Record<
  StatsObjectType,
  {
    title: string;
    subtitle: string;
    chartTitle: string;
    chartSubtitle: string;
    countKey: keyof GetDashboardDataResponse;
    tone: StatGradientTone;
    icon: ReactNode;
  }
> = {
  users: {
    title: "کاربران",
    subtitle: "تعداد کل ثبت‌نام‌ها",
    chartTitle: "نمودار کاربران",
    chartSubtitle: "تعداد کاربران در بازه انتخابی",
    countKey: "users_count",
    tone: "orange",
    icon: <Profile2User size={22} color="currentColor" variant="Bold" />,
  },
  posts: {
    title: "پست‌ها",
    subtitle: "تعداد کل پست‌ها",
    chartTitle: "نمودار پست‌ها",
    chartSubtitle: "تعداد پست‌ها در بازه انتخابی",
    countKey: "posts_count",
    tone: "pink",
    icon: <DocumentText1 size={22} color="currentColor" variant="Bold" />,
  },
  events: {
    title: "رویدادها",
    subtitle: "تعداد کل رویدادها",
    chartTitle: "نمودار رویدادها",
    chartSubtitle: "تعداد رویدادها در بازه انتخابی",
    countKey: "events_count",
    tone: "blue",
    icon: <Calendar size={22} color="currentColor" variant="Bold" />,
  },
  articles: {
    title: "مقالات",
    subtitle: "تعداد کل مقالات",
    chartTitle: "نمودار مقالات",
    chartSubtitle: "تعداد مقالات در بازه انتخابی",
    countKey: "articles_count",
    tone: "purple",
    icon: <Book size={22} color="currentColor" variant="Bold" />,
  },
  courses: {
    title: "دوره‌ها",
    subtitle: "تعداد کل دوره‌ها",
    chartTitle: "نمودار دوره‌ها",
    chartSubtitle: "تعداد دوره‌ها در بازه انتخابی",
    countKey: "courses_count",
    tone: "cyan",
    icon: <Notepad size={22} color="currentColor" variant="Bold" />,
  },
  payments: {
    title: "پرداختی‌ها",
    subtitle: "تعداد و مبلغ کل پرداختی‌ها",
    chartTitle: "نمودار پرداخت‌ها",
    chartSubtitle: "تعداد پرداخت‌ها در بازه انتخابی",
    countKey: "payments_count",
    tone: "teal",
    icon: <ExportSquare size={22} color="currentColor" variant="Bold" />,
  },
  payouts: {
    title: "برداشت‌ها",
    subtitle: "تعداد و مبلغ کل برداشت",
    chartTitle: "نمودار برداشت‌ها",
    chartSubtitle: "تعداد برداشت‌ها در بازه انتخابی",
    countKey: "payouts_count",
    tone: "yellow",
    icon: <ImportSquare size={22} color="currentColor" variant="Bold" />,
  },
};

const CARD_ORDER: StatsObjectType[] = [
  "users",
  "posts",
  "events",
  "articles",
  "courses",
  "payments",
  "payouts",
];

const toIsoDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDefaultDateRange = () => {
  const until = new Date();
  const from = new Date();
  from.setDate(until.getDate() - 30);
  return {
    fromDate: toIsoDate(from),
    untilDate: toIsoDate(until),
  };
};

const formatChartLabel = (rawDate: string) => {
  const date = new Date(rawDate.includes("T") ? rawDate : `${rawDate}T12:00:00`);
  if (Number.isNaN(date.getTime())) return rawDate;
  return date.toLocaleDateString("fa-IR");
};

const mapDaysToChartData = (
  days: Record<string, number> | undefined
): StatsBarChartDatum[] => {
  if (!days) return [];

  return Object.entries(days)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, count]) => ({
      label: formatChartLabel(date),
      value: Number(count) || 0,
    }));
};

export default function Stats() {
  const defaults = useMemo(() => getDefaultDateRange(), []);
  const [stats, setStats] = useState<GetDashboardDataResponse | null>(null);
  const [objectType, setObjectType] = useState<StatsObjectType>("articles");
  const [fromDate, setFromDate] = useState(defaults.fromDate);
  const [untilDate, setUntilDate] = useState(defaults.untilDate);
  const [appliedFromDate, setAppliedFromDate] = useState(defaults.fromDate);
  const [appliedUntilDate, setAppliedUntilDate] = useState(defaults.untilDate);
  const [chartData, setChartData] = useState<StatsBarChartDatum[]>([]);
  const [isStatsLoading, setIsStatsLoading] = useState(true);
  const [isChartLoading, setIsChartLoading] = useState(true);

  const { showLoading, hideLoading } = useAdminLoading();
  const isFirstLoad = useInitialPageLoad();

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (isFirstLoad) showLoading();
      setIsStatsLoading(true);

      try {
        const data = await getDashboardData();
        if (!cancelled) setStats(data);
      } finally {
        if (!cancelled) setIsStatsLoading(false);
        if (isFirstLoad) hideLoading();
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!appliedFromDate || !appliedUntilDate) return;

    let cancelled = false;

    const load = async () => {
      setIsChartLoading(true);

      try {
        const data = await getHomeStats({
          params: {
            object_type: objectType,
            from_date: appliedFromDate,
            until_date: appliedUntilDate,
          },
        });

        if (!cancelled) setChartData(mapDaysToChartData(data?.days));
      } finally {
        if (!cancelled) setIsChartLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [objectType, appliedFromDate, appliedUntilDate]);

  const handleRangeChange = (nextFrom: string, nextUntil: string) => {
    setFromDate(nextFrom);
    setUntilDate(nextUntil);
  };

  const handleApplyFilter = () => {
    if (!fromDate || !untilDate) return;
    setAppliedFromDate(fromDate);
    setAppliedUntilDate(untilDate);
  };

  const selectedMeta = OBJECT_META[objectType];

  return (
    <div className="flex flex-col gap-6" dir="rtl">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
        {CARD_ORDER.map((key) => {
          const meta = OBJECT_META[key];
          const value = stats?.[meta.countKey] ?? 0;

          return (
            <StatGradientCard
              key={key}
              title={meta.title}
              subtitle={meta.subtitle}
              value={isStatsLoading ? "—" : value}
              icon={meta.icon}
              tone={meta.tone}
              active={objectType === key}
              onClick={() => setObjectType(key)}
            />
          );
        })}
      </div>

      <StatsChartPanel
        title={selectedMeta.chartTitle}
        subtitle={selectedMeta.chartSubtitle}
        fromDate={fromDate}
        untilDate={untilDate}
        onRangeChange={handleRangeChange}
        onApplyFilter={handleApplyFilter}
        chartData={chartData}
        chartLoading={isChartLoading}
        filterLoading={isChartLoading}
        emptyText="در این بازه داده‌ای یافت نشد"
      />
    </div>
  );
}
